import { CText } from '@bm-js/atlascloud-ui'
import { Card, CardBody, CardHeader } from '@heroui/card'
import { Chip } from '@heroui/chip'
import { useWebsite, useWebsiteStatus } from '../utils/hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import LinkButton from './LinkButton'
import ImageWithLoader from './ImageWithLoader'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { isCmsWebsite, LucideIconName } from '@bm-js/atlascloud-shared'
import NetlifyStatusBadge from './NetlifyStatusBadge'

const WebsiteCard = () => {
  const { anyWebsite } = useWebsite()
  const { tr } = useTr()
  const { deployStatus, screenshot, siteLink } = useWebsiteStatus()

  const cardHeader = useMemo(() => {
    if (!anyWebsite) return ''
    if (anyWebsite.customDomain) return anyWebsite.customDomain
    return anyWebsite.url?.replace('https://', '') || anyWebsite.viewName
  }, [anyWebsite])

  const editorLink = useMemo(() => {
    if (!anyWebsite) return ''
    if (isCmsWebsite(anyWebsite)) return `/cms-editor/${anyWebsite._id}`
    return `/editor/${anyWebsite._id}`
  }, [anyWebsite])

  const liveWebsiteLink = useMemo(() => {
    if (!anyWebsite) return
    if (siteLink) return siteLink
    if (isCmsWebsite(anyWebsite) && anyWebsite.customDomain) {
      return `https://${anyWebsite.customDomain}`
    }
  }, [anyWebsite, siteLink])

  if (!anyWebsite) return null

  return (
    <div>
      <CText tag='h1' className='text-2xl mb-4'>
        {tr(TrKey.dashboardWebsite)}
      </CText>
      <Card className='p-3 pt-1'>
        <CardHeader className='flex justify-between'>
          <div>
            <CText tag='h2' className='text-xl'>
              {cardHeader}
            </CText>
            {liveWebsiteLink && (
              <a href={liveWebsiteLink} target='_blank' rel='noreferrer'>
                <LinkButton>{tr(TrKey.showLiveWebsite)}</LinkButton>
              </a>
            )}
          </div>
          <Link to={editorLink} className='clean'>
            <LinkButton icon={LucideIconName.Pen}>
              {tr(TrKey.editWebsite)}
            </LinkButton>
          </Link>
        </CardHeader>
        <CardBody>
          {isCmsWebsite(anyWebsite) && anyWebsite.statusBadgeUrl && (
            <NetlifyStatusBadge badgeUrl={anyWebsite.statusBadgeUrl} />
          )}
          {!isCmsWebsite(anyWebsite) && (
            <div className='flex gap-4 md:items-center md:flex-row flex-col'>
              <ImageWithLoader
                src={screenshot}
                alt='Website screenshot'
                className='rounded-lg md:w-36 w-full'
              />
              <div>
                <div className='flex gap-4 items-center mb-2'>
                  <CText className='text-neutral-700'>{cardHeader}</CText>
                  <Chip variant='flat' color={deployStatus.color}>
                    Status: {tr(deployStatus.text)}
                  </Chip>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default WebsiteCard
